import { type ReactElement, useEffect, useRef, useState } from 'react';

/**
 * A figure that counts up to its value when it appears (« 0 → 2 819,499 DT »), and glides from the old value to the
 * new one when it changes (a live update). The text keeps its exact formatting — group separator, decimal comma,
 * number of decimals, unit around the number — so `formatCatalogPrice` and friends stay the single source of formats.
 *
 * No animation (the final text at once) when the device asks for reduced motion, or where `matchMedia` /
 * `requestAnimationFrame` do not exist (tests, server rendering).
 */

const DURATION_MS = 1_000;
// The first number of the text: digits with optional whitespace group separators (« 2 819 », including the no-break
// spaces the number formats use — JavaScript's \s matches them), then optional decimals after a dot or a comma.
const NUMBER = /-?\d{1,3}(?:\s\d{3})+(?:[.,]\d+)?|-?\d+(?:[.,]\d+)?/u;

interface ParsedNumber {
  readonly value: number;
  readonly decimals: number;
  readonly decimalChar: string;
  readonly groupChar: string | null;
  readonly before: string;
  readonly after: string;
}

export function parseFigure(text: string): ParsedNumber | null {
  const match = NUMBER.exec(text);
  if (match === null) return null;
  const raw = match[0];
  const decimalMatch = /[.,](\d+)$/u.exec(raw);
  const groupMatch = /\d(\s)\d{3}/u.exec(raw);
  const decimals = decimalMatch?.[1]?.length ?? 0;
  const digits = raw.replace(/\s/gu, '').replace(',', '.');
  const value = Number(digits);
  if (!Number.isFinite(value)) return null;
  return {
    value,
    decimals,
    decimalChar: decimalMatch?.[0].charAt(0) ?? ',',
    groupChar: groupMatch?.[1] ?? null,
    before: text.slice(0, match.index),
    after: text.slice(match.index + raw.length),
  };
}

export function formatLike(parsed: ParsedNumber, value: number): string {
  const fixed = Math.abs(value).toFixed(parsed.decimals);
  const [integer = '0', fraction] = fixed.split('.');
  const grouped =
    parsed.groupChar === null
      ? integer
      : integer.replace(/\B(?=(\d{3})+(?!\d))/gu, parsed.groupChar);
  const sign = value < 0 ? '-' : '';
  const number = fraction === undefined ? grouped : `${grouped}${parsed.decimalChar}${fraction}`;
  return `${parsed.before}${sign}${number}${parsed.after}`;
}

function motionAllowed(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  if (typeof window.requestAnimationFrame !== 'function') return false;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

const easeOutCubic = (x: number): number => 1 - (1 - x) ** 3;

export function CountUp({ value }: { value: string }): ReactElement {
  const parsed = parseFigure(value);
  const target = parsed?.value ?? null;
  const fromRef = useRef(0);
  const [shown, setShown] = useState<string>(() =>
    parsed !== null && motionAllowed() ? formatLike(parsed, 0) : value,
  );

  useEffect(() => {
    if (parsed === null || target === null || !motionAllowed()) {
      setShown(value);
      return undefined;
    }
    const from = fromRef.current;
    const started = performance.now();
    let frame = 0;
    const step = (now: number): void => {
      const k = Math.min(1, (now - started) / DURATION_MS);
      const current = from + (target - from) * easeOutCubic(k);
      setShown(k >= 1 ? value : formatLike(parsed, current));
      if (k < 1) frame = requestAnimationFrame(step);
      else fromRef.current = target;
    };
    frame = requestAnimationFrame(step);
    return () => {
      cancelAnimationFrame(frame);
    };
    // `parsed` and `target` are derived from `value`: re-running on `value` covers them.
  }, [value]);

  return <>{shown}</>;
}
